import { ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/ui/Button";

/**
 * The screen shown whenever the application refuses to start.
 *
 * Issue #4 requires missing auth or runtime configuration to fail closed with a
 * *useful* screen. Useful means: say what failed, say which values were wrong,
 * and offer the one action that might help. It never degrades into a partly
 * configured editor, and it never renders blank.
 */
export function FailClosedScreen({
  title,
  summary,
  details = [],
  actions,
  footer,
}: {
  title: string;
  summary: string;
  /** One line per invalid or missing value. Never contains PHI. */
  details?: string[];
  actions?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      role="alert"
      className="flex min-h-screen items-center justify-center bg-background px-6 py-12"
    >
      <div className="w-full max-w-xl rounded-md border border-border bg-card p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive"
          >
            <ShieldAlert className="h-5 w-5" />
          </span>
          <div className="min-w-0 space-y-2">
            <h1 className="text-lg font-semibold text-foreground">{title}</h1>
            <p className="text-sm text-muted-foreground">{summary}</p>
          </div>
        </div>

        {details.length > 0 ? (
          <div className="mt-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Details
            </h2>
            <ul className="mt-2 space-y-1">
              {details.map((detail) => (
                <li
                  key={detail}
                  className="rounded-md bg-muted px-3 py-2 font-mono text-xs text-foreground"
                >
                  {detail}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {actions ? (
          <div className="mt-6 flex flex-wrap gap-2">{actions}</div>
        ) : null}

        {footer ? (
          <p className="mt-6 border-t border-border pt-4 text-xs text-muted-foreground">
            {footer}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function RetryButton({ onRetry }: { onRetry: () => void }) {
  return (
    <Button variant="primary" onClick={onRetry}>
      Try again
    </Button>
  );
}
